import React, { useEffect, useState, useRef } from 'react';
import { Card } from './ui/card';
import { MapPin, Car, Clock, Star, Loader2, RefreshCw, X } from 'lucide-react';
import { Button } from './ui/button';
import axios from 'axios';

const NearbyDriversMap = ({ 
  pickupLat, 
  pickupLng,
  pickupAddress,
  backendUrl,
  onClose,
  selectedVehicleType
}) => {
  const [nearbyDrivers, setNearbyDrivers] = useState([]);
  const [vehicleSummary, setVehicleSummary] = useState({});
  const [driversWithoutLocation, setDriversWithoutLocation] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [mapError, setMapError] = useState(false);
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef([]);

  // Fetch nearby drivers
  const fetchNearbyDrivers = async () => {
    if (!pickupLat || !pickupLng) return;
    
    setIsLoading(true);
    try {
      const params = new URLSearchParams({
        lat: pickupLat,
        lng: pickupLng
      });
      if (selectedVehicleType) {
        params.append('vehicle_type', selectedVehicleType);
      }
      
      const response = await axios.get(`${backendUrl}/api/users/nearby-drivers?${params.toString()}`);
      setNearbyDrivers(response.data.drivers || []);
      setVehicleSummary(response.data.vehicle_summary || {});
      setDriversWithoutLocation(response.data.drivers_without_location || 0);
    } catch (error) {
      console.error('Error fetching nearby drivers:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchNearbyDrivers();
    // Refresh every 30 seconds
    const interval = setInterval(fetchNearbyDrivers, 30000);
    return () => clearInterval(interval);
  }, [pickupLat, pickupLng, selectedVehicleType, backendUrl]);

  // Initialize map
  useEffect(() => {
    if (!mapRef.current || !pickupLat || !pickupLng) return;

    try {
      if (typeof window.L === 'undefined') {
        setMapError(true);
        return;
      }

      const L = window.L;
      
      // Initialize map if not already done
      if (!mapInstanceRef.current) {
        mapInstanceRef.current = L.map(mapRef.current).setView([pickupLat, pickupLng], 14);
        
        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
          attribution: '© OpenStreetMap'
        }).addTo(mapInstanceRef.current);

        // Add pickup marker
        const pickupIcon = L.divIcon({
          className: 'custom-div-icon',
          html: `<div style="background: #1e3a5f; color: white; padding: 8px; border-radius: 50%; border: 3px solid #c9a227; box-shadow: 0 2px 8px rgba(0,0,0,0.3);">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
              <circle cx="12" cy="10" r="3"></circle>
            </svg>
          </div>`,
          iconSize: [40, 40],
          iconAnchor: [20, 40]
        });
        
        L.marker([pickupLat, pickupLng], { icon: pickupIcon })
          .addTo(mapInstanceRef.current)
          .bindPopup(`<strong>Your Pickup Location</strong><br/>${pickupAddress || 'Pickup'}`);
      } else {
        mapInstanceRef.current.setView([pickupLat, pickupLng], 14);
      }

      // Clear existing driver markers
      markersRef.current.forEach(marker => {
        mapInstanceRef.current.removeLayer(marker);
      });
      markersRef.current = [];

      // Add driver markers with J-Style logo and ETA time
      nearbyDrivers.forEach(driver => {
        // Create custom marker with actual J-Style logo and ETA on top (slightly smaller)
        const carIcon = L.divIcon({
          className: 'custom-div-icon',
          html: `<div style="position: relative; display: flex; flex-direction: column; align-items: center;">
            <div style="
              background: #1e3a5f; 
              color: #c9a227; 
              padding: 1px 6px; 
              border-radius: 10px; 
              font-size: 10px; 
              font-weight: bold;
              white-space: nowrap;
              margin-bottom: 2px;
              box-shadow: 0 2px 4px rgba(0,0,0,0.3);
              border: 1px solid #c9a227;
            ">${driver.eta_minutes} min</div>
            <div style="
              width: 36px;
              height: 36px;
              border-radius: 50%;
              border: 2px solid #c9a227;
              box-shadow: 0 2px 6px rgba(0,0,0,0.4);
              overflow: hidden;
              background: #1e3a5f;
            ">
              <img src="/jstyle-logo-marker.png" style="width: 100%; height: 100%; object-fit: cover;" alt="J-Style" />
            </div>
          </div>`,
          iconSize: [42, 54],
          iconAnchor: [21, 54]
        });

        const marker = L.marker([driver.lat, driver.lng], { icon: carIcon })
          .addTo(mapInstanceRef.current)
          .bindPopup(`
            <div style="min-width: 150px;">
              <strong>${driver.vehicle_type}</strong><br/>
              <span style="color: #666;">${driver.vehicle_model || ''}</span><br/>
              <div style="margin-top: 5px; display: flex; align-items: center; gap: 5px;">
                <span style="color: #c9a227;">★</span> ${driver.rating?.toFixed(1) || '4.5'}
              </div>
              <div style="margin-top: 5px; font-weight: bold; color: #1e3a5f;">
                ${driver.eta_minutes} min away
              </div>
            </div>
          `);
        
        markersRef.current.push(marker);
      });

      // Fit bounds to show all markers
      if (nearbyDrivers.length > 0) {
        const allPoints = [[pickupLat, pickupLng], ...nearbyDrivers.map(d => [d.lat, d.lng])];
        const bounds = L.latLngBounds(allPoints);
        mapInstanceRef.current.fitBounds(bounds, { padding: [30, 30] });
      }

    } catch (error) {
      console.error('Map error:', error);
      setMapError(true);
    }
  }, [pickupLat, pickupLng, nearbyDrivers, pickupAddress]);

  // Cleanup
  useEffect(() => {
    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, []);

  return (
    <Card className="p-3 border-2 border-amber-300 bg-white shadow-md">
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-semibold text-sm flex items-center gap-2">
          <Car className="text-amber-500" size={16} />
          Nearby Drivers
        </h3>
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={fetchNearbyDrivers}
          disabled={isLoading}
          className="h-7 w-7 p-0"
        >
          <RefreshCw size={14} className={isLoading ? 'animate-spin' : ''} />
        </Button>
      </div>

      {/* Vehicle Summary - Compact */}
      {Object.keys(vehicleSummary).length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-2">
          {Object.entries(vehicleSummary).map(([type, info]) => (
            <div 
              key={type} 
              className="bg-slate-100 px-2 py-1 rounded-full text-xs flex items-center gap-1"
            >
              <span className="font-medium truncate max-w-[100px]">{type}</span>
              <span className="text-gray-500">({info.count})</span>
              <span className="text-green-600 font-medium">{info.min_eta}m</span>
            </div>
          ))}
        </div>
      )}

      {/* Map - Responsive height */}
      <div className="relative">
        {isLoading && (
          <div className="absolute inset-0 bg-white/80 z-10 flex items-center justify-center rounded-lg">
            <Loader2 className="animate-spin text-amber-500" size={24} />
          </div>
        )}
        
        {mapError ? (
          <div className="h-48 sm:h-52 bg-gray-100 rounded-lg flex items-center justify-center">
            <div className="text-center text-gray-500">
              <MapPin size={24} className="mx-auto mb-2 opacity-50" />
              <p className="text-sm">Map unavailable</p>
            </div>
          </div>
        ) : (
          <div 
            ref={mapRef} 
            className="h-48 sm:h-52 rounded-lg border border-gray-200"
            style={{ zIndex: 1 }}
          />
        )}
      </div>

      {/* Simple status message - No driver list */}
      {!isLoading && nearbyDrivers.length === 0 && driversWithoutLocation === 0 && (
        <div className="mt-2 text-center py-2 text-gray-500">
          <p className="text-sm">No drivers available nearby</p>
        </div>
      )}
      {!isLoading && driversWithoutLocation > 0 && nearbyDrivers.length === 0 && (
        <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded-lg text-center">
          <p className="text-xs text-blue-700">
            <span className="font-medium">{driversWithoutLocation} driver{driversWithoutLocation > 1 ? 's' : ''}</span> online (updating location)
          </p>
        </div>
      )}
    </Card>
  );
};

export default NearbyDriversMap;
